import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const WebSocketContext = createContext(null);

// Custom event for data refresh
const DATA_REFRESH_EVENT = 'ofpilot_data_refresh';

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [newMessages, setNewMessages] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [conversationUpdates, setConversationUpdates] = useState([]);
  const [typingIndicators, setTypingIndicators] = useState({});
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  // Get WebSocket URL from environment — includes user_id and token (WS can't use cookies)
  const getWsUrl = useCallback(() => {
    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = apiUrl.replace(/^https?:\/\//, '');
    return `${wsProtocol}://${wsHost}/ws/${user?.id}?token=${token}`;
  }, [token, user?.id]);

  const connect = useCallback(() => {
    if (!token || !user) return;
    
    // Don't connect if already connected
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      const wsUrl = getWsUrl();
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        reconnectAttempts.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleMessage(data);
        } catch (e) {
          console.error('WebSocket message parse error:', e);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        setIsConnected(false);
        wsRef.current = null;

        // Attempt reconnection
        if (reconnectAttempts.current < maxReconnectAttempts && token) {
          reconnectAttempts.current++;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current})`);
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        }
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('WebSocket connection error:', error);
    }
  }, [token, user, getWsUrl]);

  const handleMessage = useCallback((data) => {
    const { event, data: payload } = data;

    switch (event) {
      case 'unread_count_update':
        setUnreadCount(payload.count);
        break;

      case 'notification':
        if (payload.type === 'support_ticket') {
          break;
        }
        setNotifications(prev => [payload, ...prev]);
        setUnreadCount(prev => prev + 1);
        showNotificationToast(payload);
        break;

      case 'new_message':
        // Legacy message notification
        setNewMessages(prev => [payload, ...prev]);
        toast.info(`💬 ${payload.sender_name}`, {
          description: payload.subject || payload.content,
          duration: 5000,
        });
        break;

      case 'chat_message':
        // Real-time chat message
        setChatMessages(prev => [...prev, payload]);
        setChatUnreadCount(prev => prev + 1);
        break;

      case 'conversation_update':
        // Update conversation list
        setConversationUpdates(prev => {
          const existing = prev.findIndex(u => u.conversation_id === payload.conversation_id);
          if (existing >= 0) {
            const updated = [...prev];
            updated[existing] = payload;
            return updated;
          }
          return [...prev, payload];
        });
        break;

      case 'typing_indicator':
        // Handle typing indicators
        setTypingIndicators(prev => ({
          ...prev,
          [payload.conversation_id]: payload.is_typing ? payload : null
        }));
        break;

      case 'data_refresh':
        // Broadcast a custom event for components to listen to
        console.log('Data refresh event received:', payload);
        window.dispatchEvent(new CustomEvent(DATA_REFRESH_EVENT, { 
          detail: payload 
        }));
        
        // Show toast for certain types
        if (payload.type === 'homework_created') {
          toast.info('📝 Nouveau devoir', {
            description: payload.title,
            duration: 4000,
          });
        } else if (payload.type === 'live_session_created') {
          toast.info('📺 Session live planifiée', {
            description: payload.title,
            duration: 4000,
          });
        } else if (payload.type === 'evaluation_created') {
          toast.info('📋 Nouvelle évaluation', {
            description: payload.title,
            duration: 4000,
          });
        } else if (payload.type === 'announcement_created') {
          toast.info('📢 Nouvelle annonce', {
            description: payload.title,
            duration: 4000,
          });
        }
        break;

      default:
        console.log('Unknown WebSocket event:', event, payload);
    }
  }, []);

  const showNotificationToast = (notification) => {
    const icons = {
      nouveau_devoir: '📝',
      devoir_corrige: '✅',
      evaluation_notee: '⭐',
      nouveau_message: '💬',
      live_a_venir: '📺',
      mise_a_jour_cours: '📚',
      nouvelle_annonce: '📢',
      nouveau_document: '📄',
    };

    const icon = icons[notification.type] || '🔔';
    
    toast.info(`${icon} ${notification.title}`, {
      description: notification.message,
      duration: 5000,
    });
  };

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const sendPing = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send('ping');
    }
  }, []);

  // Clear chat message after it's been displayed
  const clearChatMessage = useCallback((messageId) => {
    setChatMessages(prev => prev.filter(m => m.id !== messageId));
  }, []);

  // Clear conversation update after processed
  const clearConversationUpdate = useCallback((conversationId) => {
    setConversationUpdates(prev => prev.filter(u => u.conversation_id !== conversationId));
  }, []);

  // Clear new message after it's been viewed
  const clearNewMessage = useCallback((messageId) => {
    setNewMessages(prev => prev.filter(m => m.id !== messageId));
  }, []);

  // Mark notification as read locally
  const markNotificationRead = useCallback((notificationId) => {
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  // Mark notification as acknowledged locally
  const markNotificationAcknowledged = useCallback((notificationId, acknowledgedAt) => {
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { 
        ...n, 
        is_read: true, 
        is_acknowledged: true,
        acknowledged_at: acknowledgedAt 
      } : n)
    );
  }, []);

  // Update unread count (called after API call)
  const updateUnreadCount = useCallback((count) => {
    setUnreadCount(count);
  }, []);

  // Update chat unread count
  const updateChatUnreadCount = useCallback((count) => {
    setChatUnreadCount(count);
  }, []);

  // Add notification to list (used when fetching from API)
  const setNotificationsList = useCallback((notifs) => {
    setNotifications(notifs);
  }, []);

  // Connect when user logs in
  useEffect(() => {
    if (token && user) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [token, user, connect, disconnect]);

  // Keep connection alive with periodic pings
  useEffect(() => {
    if (!isConnected) return;

    const pingInterval = setInterval(sendPing, 30000);
    return () => clearInterval(pingInterval);
  }, [isConnected, sendPing]);

  const value = {
    isConnected,
    unreadCount,
    chatUnreadCount,
    notifications,
    newMessages,
    chatMessages,
    conversationUpdates,
    typingIndicators,
    connect,
    disconnect,
    clearNewMessage,
    clearChatMessage,
    clearConversationUpdate,
    markNotificationRead,
    markNotificationAcknowledged,
    updateUnreadCount,
    updateChatUnreadCount,
    setNotificationsList,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketContext;

// Export the event name for components to use
export { DATA_REFRESH_EVENT };
